"use client";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LanguageSwitcher } from "@/components/ui/language-switcher";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/lib/supabase";
import { zodResolver } from "@hookform/resolvers/zod";
import { AlertCircle, Loader2 } from "lucide-react";
import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  Link,
  useNavigate,
  useSearchParams,
  useLocation
} from "react-router-dom";
import * as z from "zod";
import { useAuth } from "../context/AuthContext";

const getUserRole = (userType: string) => {
  if (userType === "talent") {
    return "talent";
  } else if (userType === "employer") {
    return "admin";
  } else if (userType === "partner") {
    return "partner";
  }
};
const createFormSchema = (t: any) =>
  z
    .object({
      email: z.string().email(t("auth:errors.email_invalid")),
      password: z.string().min(6, t("auth:errors.password_too_short")),
      confirmPassword: z.string(),
      userType: z.enum(["talent", "employer", "partner"], {
        errorMap: () => ({ message: t("auth:errors.user_type_required") })
      }),
      acceptTerms: z.boolean().refine((val) => val === true, {
        message: t("auth:errors.terms_required")
      })
    })
    .refine((data) => data.password === data.confirmPassword, {
      message: t("auth:errors.passwords_no_match"),
      path: ["confirmPassword"]
    });

type FormData = {
  email: string;
  password: string;
  confirmPassword: string;
  userType: string;
  acceptTerms: boolean;
};

export default function Signup() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { signUp, profile, loading: authLoading } = useAuth();
  const { t } = useTranslation(["auth", "common"]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!authLoading && !profile.anonymous) {
      navigate("/");
    }
  }, [authLoading, profile, navigate]);
  const [searchParams] = useSearchParams();
  const referralCode = searchParams.get("referral");
  // CompanyId and PartnerId are present if the user is being invited by a company or partner.
  const companyId = searchParams.get("company");
  const partnerId = searchParams.get("partner");
  const location = useLocation();
  const formSchema = createFormSchema(t);
  const initialUserType = () => {
    const match = location.pathname.match(/^\/signup\/?(.*)?$/);
    return match?.[1] || "";
  };
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch
  } = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      userType: initialUserType()
    }
  });
  const acceptTerms = watch("acceptTerms");
  const userType = watch("userType");
  // This does not trigger any re-rendering and only keeps the url in sync with the selected option.
  const currentUrl = new URL(window.location.href);
  currentUrl.pathname = `/signup/${userType}`;
  window.history.replaceState(null, "", currentUrl.toString());
  const onSubmit = async (data: FormData) => {
    setIsLoading(true);
    setError(null);
    try {
      const role = getUserRole(userType);
      await signUp(data.email, data.password, {
        data: {
          referral_code: referralCode,
          role,
          company_id: role === "admin" ? companyId : null,
          partner_id: role === "partner" || role === "talent" ? partnerId : null
        }
      });
      // Get the created user
      toast({
        title: t("common:status.success"),
        description: t("auth:success.signup")
      });
      navigate("/login");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Signup failed");
      toast({
        title: t("common:status.error"),
        description: "Signup failed. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSSOSignup = async (provider: "google" | "linkedin") => {
    setIsLoading(true);
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider,
        options: {
          redirectTo: `${window.location.origin}/auth/callback`
        }
      });
      if (error) throw error;
    } catch (err) {
      setError(err instanceof Error ? err.message : "SSO signup failed");
      toast({
        title: t("common:status.error"),
        description: "SSO signup failed",
        variant: "destructive"
      });
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Signup form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-4 bg-gray-50 relative">
        <div className="absolute top-4 right-4">
          <LanguageSwitcher />
        </div>

        <div className="w-full max-w-md bg-white rounded-lg shadow-sm p-6 space-y-6">
          <div className="lg:hidden mb-8 flex items-center gap-2">
            <div className="h-10 w-10 rounded-md bg-[#d769b3] flex items-center justify-center">
              <span className="text-[#471999] font-semibold">W</span>
            </div>
            <h1 className="text-xl font-semibold">Workinger</h1>
          </div>
          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          <div className="text-center space-y-2">
            <h2 className="text-2xl font-bold">{t("auth:signup.title")}</h2>
            <p className="text-sm text-muted-foreground">
              {t("auth:signup.subtitle")}
            </p>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">{t("common:form.email")}</Label>
              <Input
                id="email"
                type="email"
                placeholder={t("auth:login.email_placeholder")}
                {...register("email")}
                disabled={isLoading}
              />
              {errors.email && (
                <p className="text-sm text-destructive">
                  {errors.email.message}
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">{t("common:form.password")}</Label>
              <Input
                id="password"
                type="password"
                placeholder={t("auth:login.password_placeholder")}
                {...register("password")}
                disabled={isLoading}
              />
              {errors.password && (
                <p className="text-sm text-destructive">
                  {errors.password.message}
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">
                {t("common:form.confirm_password")}
              </Label>
              <Input
                id="confirmPassword"
                type="password"
                placeholder={t("common:form.confirm_password")}
                {...register("confirmPassword")}
                disabled={isLoading}
              />
              {errors.confirmPassword && (
                <p className="text-sm text-destructive">
                  {errors.confirmPassword.message}
                </p>
              )}
            </div>
            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <Button
                  type="button"
                  variant={userType === "talent" ? "default" : "outline"}
                  disabled={isLoading}
                  onClick={() => setValue("userType", "talent")}
                  className={`w-full ${userType === "talent" ? "bg-[#d769b3] text-white" : ""}`}
                >
                  {t("auth:user_types.talent")}
                </Button>
                <Button
                  type="button"
                  variant={userType === "employer" ? "default" : "outline"}
                  onClick={() => setValue("userType", "employer")}
                  disabled={isLoading}
                  className={`w-full ${userType === "employer" ? "bg-[#d769b3] text-white" : ""}`}
                >
                  {t("auth:user_types.employer")}
                </Button>
                <Button
                  type="button"
                  variant={userType === "partner" ? "default" : "outline"}
                  onClick={() => setValue("userType", "partner")}
                  disabled={isLoading}
                  className={`w-full ${userType === "partner" ? "bg-[#d769b3] text-white" : ""}`}
                >
                  {t("auth:user_types.partner")}
                </Button>
              </div>
              {errors.userType && (
                <p className="text-sm text-destructive">
                  {errors.userType.message}
                </p>
              )}
            </div>

            <div className="flex items-center space-x-2">
              <Checkbox
                id="acceptTerms"
                checked={acceptTerms}
                onCheckedChange={(checked) =>
                  setValue("acceptTerms", !!checked)
                }
                disabled={isLoading}
              />
              <Label htmlFor="acceptTerms" className="text-sm">
                {t("auth:signup.terms")}{" "}
                <Link to="/terms" className="text-primary hover:underline">
                  {t("auth:signup.terms_link")}
                </Link>
              </Label>
            </div>
            {errors.acceptTerms && (
              <p className="text-sm text-destructive">
                {errors.acceptTerms.message}
              </p>
            )}

            <Button
              type="submit"
              className="w-full bg-[#471999] hover:bg-[#471999]/90"
              disabled={isLoading || !acceptTerms}
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t("common:status.loading")}
                </>
              ) : (
                t("auth:signup.submit")
              )}
            </Button>
          </form>
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-background px-2 text-muted-foreground">
                {t("auth:login.sso_divider")}
              </span>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Button
              variant="outline"
              onClick={() => handleSSOSignup("google")}
              disabled={isLoading}
              className="w-full"
            >
              <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
                <path
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  fill="#4285F4"
                />
                <path
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  fill="#34A853"
                />
                <path
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  fill="#FBBC05"
                />
                <path
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  fill="#EA4335"
                />
                <path d="M1 1h22v22H1z" fill="none" />
              </svg>
              Google
            </Button>
            <Button
              variant="outline"
              onClick={() => handleSSOSignup("linkedin")}
              disabled={isLoading}
              className="w-full"
            >
              <svg
                className="mr-2 h-4 w-4"
                fill="currentColor"
                viewBox="0 0 24 24"
              >
                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
              </svg>
              LinkedIn
            </Button>
          </div>
          <div className="text-center text-sm">
            <span className="text-gray-600">
              {t("auth:signup.have_account")}{" "}
            </span>
            <Link
              to="/login"
              className="text-primary hover:underline font-medium"
            >
              {t("auth:signup.login_link")}
            </Link>
          </div>
        </div>
      </div>

      {/* Right side - Branded background */}
      <div className="hidden lg:flex lg:w-1/2 bg-[#d769b3] items-center justify-center p-8">
        <div className="max-w-md text-white space-y-6">
          <div className="flex items-center gap-2">
            <div className="h-12 w-12 rounded-md bg-[#471999] flex items-center justify-center">
              <span className="text-[#d769b3] font-semibold text-xl">W</span>
            </div>
            <h1 className="text-2xl font-semibold text-[#471999]">Workinger</h1>
          </div>
          <h2 className="text-4xl font-bold">{t("auth:welcome.title")}</h2>
          <p className="text-lg text-indigo-100">
            {t("auth:welcome.description")}
          </p>
        </div>
      </div>
    </div>
  );
}
